/**
 * Items list — capture fast, find fast.
 * Capture reuses the canonical CreateItemModal (with its built-in AI title
 * assist); the list is a plain consumer view over the items store.
 */

import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { CreateItemModal } from '@/components/prioritization/item-list/components/CreateItemModal';
import { CATEGORY_DOT, CATEGORY_DOT_FALLBACK } from '@/lib/itemCategoryColors';
import type { ItemExitStatus } from '@/types/prioritization/itemLifecycle';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, PrimaryButton, StatusChip } from '../components/primitives';

type StatusFilter = ItemExitStatus;

const STATUS_TABS: Array<{ key: StatusFilter; label: string }> = [
  { key: 'active', label: 'Active' },
  { key: 'deferred', label: 'Set aside' },
  { key: 'archived', label: 'Archived' },
];

export default function ItemsListScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const app = getAppDefinition('items')!;
  // Select the raw slice — getItems() builds a fresh array per call and would
  // re-render forever under reference equality.
  const items = useItemsStore((s) => s.items);
  const fetchFromSupabase = useItemsStore((s) => s.fetchFromSupabase);

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  // Arriving from Start's "get it out of my head" intent opens capture
  // immediately — the job is dumping, not browsing (PRD-start-intents §7).
  const [createOpen, setCreateOpen] = useState(
    Boolean((location.state as { startCapture?: boolean } | null)?.startCapture)
  );

  useEffect(() => {
    void fetchFromSupabase();
  }, [fetchFromSupabase]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((item) => (item.exit_status ?? 'active') === status)
      .filter(
        (item) =>
          !q ||
          item.title.toLowerCase().includes(q) ||
          (item.description ?? '').toLowerCase().includes(q)
      )
      .sort(
        (a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime()
      );
  }, [items, query, status]);

  return (
    <AppShell
      app={app}
      actions={
        <PrimaryButton onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" /> Add item
        </PrimaryButton>
      }
    >
      <ScreenHeading
        title="Your items"
        subtitle="Everything you might act on, in one place — sessions and plans draw from this list."
      />
      <BallLine ballWith="you — capture what's on your mind" />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search items…"
            className="w-full rounded-md border border-gray-300 bg-white py-2.5 pl-9 pr-3 font-body text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-pri)]"
          />
        </div>
        <div className="flex gap-1 rounded-md border border-gray-200 bg-white p-1 dark:border-[var(--rule)] dark:bg-[var(--surface)]">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setStatus(tab.key)}
              className={`rounded px-3 py-1.5 font-body text-xs font-medium ${
                status === tab.key
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50 dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {visible.length === 0 ? (
        <EmptyState
          title={query ? 'Nothing matches that search' : 'Nothing here yet'}
          hint={
            query
              ? 'Try a shorter search, or add it as a new item.'
              : status === 'active'
                ? 'Capture the first thing on your mind — a problem, an idea, a risk.'
                : 'Items you move out of the active list will show up here.'
          }
          action={
            <PrimaryButton onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Add an item
            </PrimaryButton>
          }
        />
      ) : (
        <div className="space-y-2">
          {visible.map((item) => (
            <AppCard key={item.id} onClick={() => navigate(`/apps/items/${item.id}`)} className="!p-4">
              <div className="flex items-center gap-3">
                <span
                  className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                    CATEGORY_DOT[item.itemCategory ?? ''] ?? CATEGORY_DOT_FALLBACK
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                    {item.title}
                  </p>
                  {item.description ? (
                    <p className="truncate font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                      {item.description}
                    </p>
                  ) : null}
                </div>
                {item.itemCategory ? <StatusChip label={item.itemCategory} tone="slate" /> : null}
              </div>
            </AppCard>
          ))}
        </div>
      )}

      <CreateItemModal isOpen={createOpen} onClose={() => setCreateOpen(false)} />
    </AppShell>
  );
}
